import { Button, Tooltip } from '@mantine/core';
import { sizes, variants } from '@uag/react-core';
import { useTranslation } from 'react-i18next';

import type { ProjectSummaryResponse } from 'api/models';

interface ProjectListItemProps {
    project: ProjectSummaryResponse;
    isSavePending: boolean;
    isLoadPending: boolean;
    isDeletePending: boolean;
    onLoad: () => void;
    onDelete: () => void;
}

export const ProjectListItem = ({
    project,
    isSavePending,
    isLoadPending,
    isDeletePending,
    onLoad,
    onDelete
}: ProjectListItemProps) => {
    const { t } = useTranslation();

    return (
        <div className="grid grid-cols-[1fr_180px_150px_120px] items-center gap-2 rounded-sm border border-border-light bg-background-gray-ultra-ultra-light px-3 py-2">
            {/* Project name and description */}
            <div className="min-w-0">
                    <Tooltip disabled={project.name.length <= 32} label={project.name} position="top" zIndex={12000} withArrow>
                    <div className="truncate text-sm font-semibold text-text-default" style={{ maxWidth: 240 }}>
                        {project.name}
                    </div>
                </Tooltip>
                    <Tooltip disabled={!project.description || project.description.length <= 40} label={project.description?.trim() || t('projectsNoDescription')} position="top-start" zIndex={12000} withArrow>
                    <div className="truncate text-xs text-text-light" style={{ maxWidth: 240 }}>
                        {project.description?.trim() || t('projectsNoDescription')}
                    </div>
                </Tooltip>
            </div>

            {/* Project saved date */}
            <div className="text-xs text-text-default font-mono text-center">{new Date(project.savedAt).toLocaleString()}</div>

            {/* Project statistics */}
            <div className="text-xs text-text-default pl-4">
                {`${project.articleGroupCount} ${t('projectsTypes')}`}
                <br />
                {`${project.totalInstances} ${t('projectsInstances')}`}
            </div>

            {/* Action buttons */}
            <div className="flex items-center justify-end gap-2">
                <Button
                    disabled={isSavePending}
                    loading={isLoadPending}
                    size={sizes.button.small}
                    variant={variants.button.secondary}
                    onClick={onLoad}
                >
                    {t('load')}
                </Button>
                <Button
                    className="bg-background-danger-light text-text-danger hover:bg-background-danger-light/80"
                    disabled={isSavePending}
                    loading={isDeletePending}
                    size={sizes.button.small}
                    variant={variants.button.danger}
                    onClick={onDelete}
                >
                    {t('delete')}
                </Button>
            </div>
        </div>
    );
};
